import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Minus } from 'lucide-react';
import { cn } from '../../lib/utils';

const services = [
  {
    title: "Custom Application Development",
    description: "Build scalable, efficient applications tailored to your unique business requirements. Our custom solutions are designed to integrate seamlessly with your existing infrastructure and evolve with your business.",
    image: "https://images.unsplash.com/photo-1581291518633-83b4ebd1d83e?auto=format&fit=crop&q=80"
  },
  {
    title: "Web Development",
    description: "Craft responsive, high-performance websites that captivate users and drive engagement. We focus on creating designs that are both aesthetically pleasing and functionally robust across all devices.",
    image: "https://images.unsplash.com/photo-1460925895917-afdab827c52f?auto=format&fit=crop&q=80"
  },
  {
    title: "Mobile Development",
    description: "Develop intuitive mobile applications for both iOS and Android platforms. Our mobile solutions prioritize user experience, performance, and security, ensuring a smooth and engaging journey for your customers.",
    image: "https://images.unsplash.com/photo-1512941937669-90a1b58e7e9c?auto=format&fit=crop&q=80"
  },
  {
    title: "Cloud Engineering",
    description: "Optimize your IT infrastructure with our cloud engineering services. We design secure, scalable, and cost-effective cloud solutions that improve operational efficiency and support business growth.",
    image: "https://images.unsplash.com/photo-1451187580459-43490279c0fa?auto=format&fit=crop&q=80"
  },
  {
    title: "Technology Consulting",
    description: "Leverage our expert guidance to navigate the digital landscape. We offer strategic advice and hands-on support to help you harness technology for innovation and competitive advantage.",
    image: "https://images.unsplash.com/photo-1553877522-43269d4ea984?auto=format&fit=crop&q=80"
  }
];

const ServiceImage = ({ image, isActive }: { image: string; isActive: boolean }) => {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: isActive ? 1 : 0 }}
      transition={{ duration: 0.5 }}
      className={`absolute inset-0 ${isActive ? 'z-10' : 'z-0'}`}
    >
      <div className="relative w-full h-full rounded-xl overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-black/60 to-black/40 backdrop-blur-[2px] z-10" />
        <img
          src={image}
          alt=""
          className="w-full h-full object-cover"
        />
      </div>
    </motion.div>
  );
};

const ServiceAccordion = ({ title, description, index, isActive, onClick }: {
  title: string;
  description: string;
  index: number;
  isActive: boolean;
  onClick: () => void;
}) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: index * 0.1 }}
      className="group"
    >
      <div
        className={cn(
          "relative transition-all duration-300 rounded-lg overflow-hidden",
          "hover:bg-gradient-to-r hover:from-black/20 hover:to-transparent",
          isActive && "bg-gradient-to-r from-black/40 to-black/20 backdrop-blur-sm"
        )}
      >
        <button
          onClick={onClick}
          className="w-full px-6 py-4 flex items-center justify-between text-left"
        >
          <h3 className={cn(
            "font-sans text-xl font-light tracking-tight transition-all duration-300",
            isActive ? "text-white" : "text-gray-400",
            "group-hover:text-white"
          )}>
            {title}
          </h3>
          <motion.div
            initial={false}
            animate={{ 
              rotate: isActive ? 180 : 0,
              scale: isActive ? 1 : 1
            }}
            transition={{ duration: 0.2, ease: "easeInOut" }}
            className={cn(
              "w-10 h-10 flex items-center justify-center rounded-lg transition-colors duration-300",
              isActive ? "bg-white/10" : "bg-black/20",
              "group-hover:bg-white/20"
            )}
          >
            {isActive ? (
              <Minus className="text-white w-5 h-5" />
            ) : (
              <Plus className="text-white w-5 h-5" />
            )}
          </motion.div>
        </button>
        <AnimatePresence initial={false}>
          {isActive && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ 
                height: "auto",
                opacity: 1,
                transition: {
                  height: {
                    duration: 0.3,
                    ease: "easeOut"
                  },
                  opacity: {
                    duration: 0.2,
                    delay: 0.1
                  }
                }
              }}
              exit={{ 
                height: 0,
                opacity: 0,
                transition: {
                  height: {
                    duration: 0.3,
                    ease: "easeIn"
                  },
                  opacity: {
                    duration: 0.2
                  }
                }
              }}
              className="overflow-hidden"
            >
              <motion.div
                initial={{ y: 10, opacity: 0 }}
                animate={{ 
                  y: 0,
                  opacity: 1,
                  transition: {
                    duration: 0.2,
                    delay: 0.15
                  }
                }}
                exit={{ 
                  y: 10,
                  opacity: 0,
                  transition: {
                    duration: 0.2
                  }
                }}
                className="px-6 pb-6 text-gray-400 font-sans text-base font-light leading-relaxed"
              >
                {description}
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.div>
  );
};

const Services = () => {
  const [activeIndex, setActiveIndex] = React.useState<number | null>(null);

  return (
    <section className="py-16 md:py-20 bg-black">
      <div className="container mx-auto px-4">
        <div className="mb-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-4xl lg:text-5xl font-display font-semibold tracking-tight text-white mb-4">
              Our Services
            </h2>
            <p className="text-xl font-sans font-light text-gray-400">
              Empowering your business with tailored digital solutions
            </p>
          </motion.div>
        </div>

        <div className="grid lg:grid-cols-[40%_60%] gap-12">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
            className="relative h-[400px]"
          >
            <ServiceImage
              image={services[0].image}
              isActive={activeIndex === null}
            />
            <AnimatePresence>
              {activeIndex !== null && (
                <ServiceImage
                  key={services[activeIndex].title}
                  image={services[activeIndex].image}
                  isActive={true}
                />
              )}
            </AnimatePresence>
          </motion.div>

          <div className="space-y-3">
            {services.map((service, index) => (
              <ServiceAccordion
                key={service.title}
                title={service.title}
                description={service.description}
                index={index}
                isActive={activeIndex === index}
                onClick={() => setActiveIndex(activeIndex === index ? null : index)}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Services;